import type { Dispatch, SetStateAction } from "react";
import type { OrganizationItem } from "../../../../types/organization";

export type OrganizationIdsState = {
  m_organization_ids: number[] | null;
};

export type OrganizationTreeItem = OrganizationItem & {
  level?: number;
  parent_m_organization_id?: number | null;
  m_branch_name?: string;
};

export type SetOrganizationIds = Dispatch<SetStateAction<OrganizationIdsState>>;

export const getParentOrganizationId = (
  organization: OrganizationTreeItem
): number | null => {
  const parentId = organization.parent_m_organization_id;
  return typeof parentId === "number" ? parentId : null;
};

export const flattenOrganizations = (
  orgs: OrganizationTreeItem[]
): OrganizationTreeItem[] => {
  let flat: OrganizationTreeItem[] = [];
  orgs.forEach((org) => {
    flat.push(org);
    if (org.child_m_organizations && org.child_m_organizations.length > 0) {
      flat = flat.concat(
        flattenOrganizations(org.child_m_organizations as OrganizationTreeItem[])
      );
    }
  });
  return flat;
};

export const flattenOrganizationIds = (org: OrganizationTreeItem): number[] => {
  let ids: number[] = [];
  if (org.id) {
    ids.push(org.id);
  }
  if (Array.isArray(org.child_m_organizations)) {
    org.child_m_organizations.forEach((childOrg) => {
      ids = ids.concat(
        flattenOrganizationIds(childOrg as OrganizationTreeItem)
      );
    });
  }
  return ids;
};

export const checkRecursiveParents = (
  parentId: number | null,
  organizations: OrganizationTreeItem[],
  updatedIds: number[],
  recursionCount = 1
): number[] => {
  if (!parentId) {
    return updatedIds;
  }

  const flatOrganizations = flattenOrganizations(organizations);

  const someChildrenChecked = flatOrganizations
    .filter((org) => org.parent_m_organization_id === parentId)
    .some((child) => updatedIds.includes(child.id));

  let nextIds = updatedIds;
  if (someChildrenChecked) {
    nextIds = Array.from(new Set([...updatedIds, parentId]));
  } else {
    nextIds = updatedIds.filter((id) => id !== parentId);
  }

  const parentOrg = flatOrganizations.find((org) => org.id === parentId);
  if (parentOrg && parentOrg.parent_m_organization_id !== null) {
    const parentOrgParentId = getParentOrganizationId(parentOrg);
    nextIds = checkRecursiveParents(
      parentOrgParentId,
      organizations,
      nextIds,
      recursionCount + 1
    );
  }

  return nextIds;
};

export const hasLevelOne = (organization: OrganizationTreeItem): boolean => {
  if (!organization) {
    return false;
  }

  if (organization.level === 1) {
    return true;
  }

  if (Array.isArray(organization.child_m_organizations)) {
    return organization.child_m_organizations.some((childOrg) =>
      hasLevelOne(childOrg as OrganizationTreeItem)
    );
  }

  return false;
};

export const getOrganizationLabel = (
  organization: OrganizationTreeItem
): string => {
  if (organization.group_name === null || organization.group_name === undefined) {
    return organization.department_name ?? "";
  }
  return `${organization.department_name ?? ""}-${organization.group_name}`;
};
